import { z } from 'zod';
import { CreateCustomFieldSchema } from './custom-field.dto';
import { CreateCustomerSchema } from './customer.dto';
import { CreateFeedbackSchema } from './feedback.dto';
import { CreateIdeaSchema } from './idea.dto';
import { inferApiResultSchema } from './infer-api';
import { ProductAreaSchema } from './product-area.dto';

export const ImportTypes = {
  Customers: 'customers',
  Ideas: 'ideas',
  Feedbacks: 'feedbacks',
} as const;

export type ImportTypesType = (typeof ImportTypes)[keyof typeof ImportTypes];

/* Bulk Import */
export const CreateBulkImportSchema = z.object({
  configS3Key: z.string(),
  itemsS3Key: z.string(),
});
export type CreateBulkImportDto = z.infer<typeof CreateBulkImportSchema>;

export const CreateBulkImportResultsSchema = inferApiResultSchema(z.array(z.number()));
export type CreateBulkImportResultsDto = z.infer<typeof CreateBulkImportResultsSchema>;

export const CreateBulkImportResponseSchema = inferApiResultSchema(
  z.object({
    imported: z.number(),
  }),
);
export type CreateBulkImportResponseDto = z.infer<typeof CreateBulkImportResponseSchema>;

export const BulkImportProductAreaSchema = ProductAreaSchema.omit({
  accountId: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
  deletedAt: true,
  permissions: true,
}).extend({
  productImageKey: z.string().nullish(),
});
export type BulkImportProductAreaDto = z.infer<typeof BulkImportProductAreaSchema>;

export const BulkImportConfigSchema = z.object({
  newCustomFields: z.array(CreateCustomFieldSchema),
  productAreas: z.array(BulkImportProductAreaSchema),
  feedbackFormName: z.string().min(1),
  originalFilename: z.string().min(1),
  originalBytes: z.number().min(1),
});
export type BulkImportConfigDto = z.infer<typeof BulkImportConfigSchema>;

export const BulkImportCustomersSchema = z.object({
  config: BulkImportConfigSchema,
  items: z.array(CreateCustomerSchema),
});

export type BulkImportCustomersDto = z.infer<typeof BulkImportCustomersSchema>;

export const BulkImportIdeasSchema = z.object({
  config: BulkImportConfigSchema,
  items: z.array(CreateIdeaSchema),
});

export type BulkImportIdeasDto = z.infer<typeof BulkImportIdeasSchema>;

export const BulkImportFeedbackSchema = z.object({
  config: BulkImportConfigSchema,
  items: z.array(CreateFeedbackSchema),
});

export type BulkImportFeedbackDto = z.infer<typeof BulkImportFeedbackSchema>;

/* Import Jobs */
export const ImportType = z.enum(['CUSTOMERS', 'IDEAS', 'FEEDBACKS']);
export const getZodImportType = (importType: ImportTypesType) => {
  return ImportType.parse(importType.toLocaleUpperCase());
};

export const ImportedIdsSchema = z.object({
  customerIds: z.array(z.number()),
  ideaIds: z.array(z.number()),
  feedbackIds: z.array(z.number()),
  customFieldIds: z.array(z.number()),
  productAreaIds: z.array(z.number()),
  feedbackFormId: z.number().nullish(),
});
export type ImportedIdsDto = z.infer<typeof ImportedIdsSchema>;

export const ImportJobSchema = z.object({
  id: z.number(),
  accountId: z.string(),
  userId: z.string(),
  type: ImportType,
  itemCount: z.number(),
  importedIds: ImportedIdsSchema,
  config: BulkImportConfigSchema,
  createdAt: z.date(),
  deletedAt: z.date().nullish(),
});

export const GetImportJobResponseSchema = inferApiResultSchema(ImportJobSchema);
export type GetImportJobResponseDto = z.infer<typeof GetImportJobResponseSchema>;

export const CreateImportJobSchema = ImportJobSchema.omit({
  id: true,
  createdAt: true,
  deletedAt: true,
});
export type CreateImportJobDto = z.infer<typeof CreateImportJobSchema>;

export const CreateImportJobResponseSchema = inferApiResultSchema(CreateImportJobSchema);
export type CreateImportJobResponseDto = z.infer<typeof CreateImportJobResponseSchema>;

export const DeleteImportJobResponseSchema = inferApiResultSchema(ImportJobSchema);
export type DeleteImportJobResponseDto = z.infer<typeof DeleteImportJobResponseSchema>;
